/** @domain employment -- Employment relationship fields: employee ID, job title, department, division, termination. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces } from './formatters';

const define = createDomainField('employment');

export const employeeId = define({
  label: 'Employee ID',
  schema: z.string().regex(/^[a-zA-Z0-9_\-/ ]+$/, 'Must be alphanumeric or _ or - or /'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const employerName = define({
  label: 'Employer Name',
  placeholder: 'Enter employer name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const jobTitle = define({
  label: 'Job Title',
  placeholder: 'Select',
  schema: z.string().max(200, 'Must be at most 200 characters'),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const department = define({
  label: 'Department',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const division = define({
  label: 'Division/Department',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const costCenter = define({
  label: 'Cost Center',
  schema: z.string().regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const divisionName = define({
  label: 'Division',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const employmentDateRange = define({
  label: 'Date Range',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'text',
  emptyText: '',
});

export const payrollId = define({
  label: 'Payroll ID',
  schema: z
    .string()
    .max(100, 'Must be less than 100 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const employeeType = define({
  label: 'Employee Type',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const roleName = define({
  label: 'Role',
  placeholder: 'Enter role name',
  schema: z.string().max(200, 'Must be at most 200 characters'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const terminationDate = define({
  label: 'Termination Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const terminationReason = define({
  label: 'Termination Reason',
  placeholder: 'Enter reason',
  schema: z.string().max(500, 'Must be at most 500 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const terminationTypeId = define({
  label: 'Termination Type',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const oldDefaultSettlementType = define({
  label: 'Settlement Type',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const settlementType = define({
  label: 'Default Tax Settlement Method',
  placeholder: 'Select',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const electionWindowStart = define({
  label: 'Election Window Starts',
  schema: z.number().int().min(1, 'Must be greater than 0'),
  adapterHint: 'number',
  displayFormat: 'number',
  adapterProps: { endAddon: 'DAYS' },
});

export const electionWindowEnd = define({
  label: 'Election Window Ends',
  schema: z.number().int().min(1, 'Must be greater than 0'),
  adapterHint: 'number',
  displayFormat: 'number',
  adapterProps: { endAddon: 'DAYS' },
});

export const settlementMethods = define({
  label: 'Tax Settlement Methods Available',
  placeholder: 'Select',
  schema: z.array(z.string()),
  adapterHint: 'async-select',
  displayFormat: 'text',
  /** Checkbox multi presentation — same fetch/value semantics as chip async multi. */
  adapterProps: { multiVariant: 'checkbox' },
});

export const isRetired = define({
  label: 'Retirement Eligibility Status',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'boolean',
});

export const retirementEligibilityDate = define({
  label: 'Retirement Eligibility Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});
